import { FC, MutableRefObject, useRef, isValidElement, Children, cloneElement, CSSProperties, } from 'react';
import { Form, Row } from 'antd';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { SearchFormItemProps } from './index';
import { filterObjEmptyValue, filterStrSpace } from '../utils/utils';
import { getDataFormats } from './util';
import { SearchFormItemEnum, filterSpaceType } from './interface/baseInterface';
import { FormInstance, FormProps } from 'antd/lib/form';
import { ColProps } from 'antd/lib/col';
import SearchFormContext from './context/searchFormContext';
import {
  DEFAULT_DATA_FORMAT,
  DETAULT_TIME_FORMAT,
} from '../constants/constant';
import './style.less';

const rowStyle: CSSProperties = { marginLeft: 0, marginRight: 0, };
export type SearchFormType = 'search' | 'detail';
export type NamePath = string | number | (string | number)[];
export interface ISearchFormContext {
  form: FormInstance;
}
// export interface SearchFormActionType {
//   onSubmit: () => void;
//   resetFields: (fields?: NamePath[]) => void;
// }
export interface SearchFormProps extends FormProps {
  /** 测试ID */
  testId?: string;
  /** 样式 */
  style?: CSSProperties;
  /** 类名 */
  wrapperClassName?: string;
  /** 高级搜索表单对象 */
  highActionRef?: FormInstance;
  /** 是否过滤空字段 */
  filterEmpty?: boolean;
  /** 每行显示多少个表单 */
  defaultLineNumber?: number;
  /** 每个Item的默认labelCol配置 */
  defaultLabelCol?: ColProps;
  /** form使用场景 */
  formType?: SearchFormType;
  /** 提供给外部方法 */
  actionRef?: MutableRefObject<FormInstance | undefined>;
  /** 空格过滤规格 */
  filterSpace?: filterSpaceType;
  /** 详情内容是否要占满整个页面 */
  isFullPage?: boolean;
  /** 获取表单的值，只有tableForm有用 */
  getFieldsValue?: () => any;
  /** 点击搜索时触发 */
  onFinish?: (values: any) => void;
  /** 点击重置时触发 */
  onReset?: () => void;
}
/** from表单 */
export const SearchForm: FC<SearchFormProps> = props => {
  const {
    defaultLineNumber,
    defaultLabelCol,
    filterEmpty,
    formType,
    children,
    actionRef,
    filterSpace,
    testId,
    highActionRef,
    wrapperClassName,
    isFullPage,
    style,
    getFieldsValue,
    onFinish,
    onReset,
    ...resProps
  } = props;
  const dataFormats = useRef<any>({});
  const [form] = Form.useForm();
  // 对外接口
  if (actionRef) {
    actionRef.current = {
      ...form,
      getFieldsValue: () => {
        const values = form.getFieldsValue();
        let highValues: any = {};
        if (getFieldsValue) {
          highValues = getFieldsValue();
        };

        return formatFormData({...values, ...highValues});
      },
      resetFields: () => {
        form.resetFields();
        highActionRef?.resetFields?.();
      },
      setFieldsValue: (values: any) => {
        const newValues: any = {};
        if (values) {
          // 将时间转换成dayjs格式
          for (let [k, v] of Object.entries(values)) {
            if (dataFormats.current[k]) {
              if (Array.isArray(v)) {
                newValues[k] = v.map(item => {
                  if (dayjs.isDayjs(item)) {
                    return item;
                  }
                  return item ? dayjs(item) : undefined;
                });
              } else {
                if (dayjs.isDayjs(v)) {
                  newValues[k] = v;
                } else {
                  newValues[k] = v ? dayjs(v as string) : undefined;
                }
              }
            } else {
              newValues[k] = v;
            }
          }
        }
        form.setFieldsValue(newValues);
        highActionRef?.setFieldsValue?.(newValues);
      },
    };
  }
  // 处理表单值
  const formatFormData = (values: any) => {
    const searchValues = filterEmpty ? filterObjEmptyValue(values) : values;
    for (let [key, value] of Object.entries(values)) {
      const currentFormat = dataFormats.current[key];
      if (dayjs.isDayjs(value)) {
        const dataFormatStr = currentFormat?.beforeName
          ? currentFormat[values[currentFormat?.beforeName]]
          : currentFormat;
        searchValues[key] = value.format(dataFormatStr || DEFAULT_DATA_FORMAT);
      }
      if (Array.isArray(value)) {
        const dataFormatStr = currentFormat?.beforeName
          ? currentFormat[values[currentFormat?.beforeName]]
          : currentFormat;
        searchValues[key] = value.map(v => {
          if (dayjs.isDayjs(v))
            return v.format(dataFormatStr || DEFAULT_DATA_FORMAT);
          return v;
        });
      }
      if (typeof value === 'string') {
        searchValues[key] = filterStrSpace(
          value,
          filterSpace as filterSpaceType,
        );
      }
    }
    return searchValues;
  };
  // 提交回调
  const handleFinish = (values: any) => {
    const newValue = formatFormData(values);
    onFinish && onFinish(newValue);
  };
  const setformats = (
    childElement: React.FunctionComponentElement<SearchFormItemProps>,
  ) => {
    const displayName = childElement.type.displayName;
    if (displayName === SearchFormItemEnum.SearchFormDatePicker) {
      const childElementProps = childElement.props;
      dataFormats.current[
        `${childElementProps.name}`
      ] = childElementProps.datePickerProps
        ? childElementProps.datePickerProps.format || DEFAULT_DATA_FORMAT
        : DEFAULT_DATA_FORMAT;
    }
    if (displayName === SearchFormItemEnum.SearchFormRangePicker) {
      const childElementProps = childElement.props;
      dataFormats.current[
        `${childElementProps.name}`
      ] = childElementProps.rangePickerProps
        ? childElementProps.rangePickerProps.format || DEFAULT_DATA_FORMAT
        : DEFAULT_DATA_FORMAT;
    }
    if (displayName === SearchFormItemEnum.SearchFormTimePicker) {
      const childElementProps = childElement.props;
      dataFormats.current[
        `${childElementProps.name}`
      ] = childElementProps.timePickerProps
        ? childElementProps.timePickerProps.format || DETAULT_TIME_FORMAT
        : DETAULT_TIME_FORMAT;
    }
    if (displayName === SearchFormItemEnum.SearchFormTimeSlot) {
      const childElementProps = childElement.props;
      const { name } = childElementProps;
      const dataFormat =
        childElementProps.datePickerProps?.format || DEFAULT_DATA_FORMAT;
      if (Array.isArray(name) && name.length === 2) {
        dataFormats.current[name[0]] = dataFormat;
        dataFormats.current[name[1]] = dataFormat;
      }
    }
    if (displayName === SearchFormItemEnum.SearchFormZoneTime) {
      const childElementProps = childElement.props;
      const { name, zoneTimeProps, zoneProps, } = childElementProps;
      if (!Array.isArray(name) || !zoneTimeProps) return;
      const beforeName = zoneProps ? name[1] : name[0];
      const afterName = zoneProps ? name[2] : name[1];
      let formatObj: any = {
        beforeName,
      };
      zoneTimeProps.options?.forEach?.(item => {
        const { timeType, value, datePickerProps, rangePickerProps, } = item;
        if (timeType === 'DatePicker' && value) formatObj[value] = datePickerProps?.format || DEFAULT_DATA_FORMAT;
        if (timeType === 'RangePicker' && value) formatObj[value] = rangePickerProps?.format || DEFAULT_DATA_FORMAT;
      });
      dataFormats.current[afterName] = formatObj;
    }
    if (displayName === SearchFormItemEnum.SearchFormSelectCustom) {
      const childElementProps = childElement.props;
      const {
        name,
        beforeFormItemProps,
        afterFormItemProps,
        options,
      } = childElementProps;
      const momentOptions = options?.filter(item =>
        ['DatePicker', 'RangePicker', 'TimePicker'].includes(
          item.afterFormItemType,
        ),
      );
      const beforeName: string = (beforeFormItemProps?.name
        ? beforeFormItemProps?.name
        : Array.isArray(name)
        ? name[0]
        : '') as string;
      const afterName: string = (afterFormItemProps?.name
        ? afterFormItemProps.name
        : Array.isArray(name)
        ? name[1]
        : '') as string;
      if (Array.isArray(momentOptions) && momentOptions.length) {
        let formatObj: any = {
          beforeName,
        };
        momentOptions.forEach(item => {
          const {
            afterFormItemType,
            value,
            datePickerProps,
            rangePickerProps,
            timePickerProps,
          } = item;
          if (afterFormItemType === 'DatePicker' && typeof value === 'string')
            formatObj[value] = datePickerProps?.format || DEFAULT_DATA_FORMAT;
          if (afterFormItemType === 'RangePicker' && typeof value === 'string')
            formatObj[value] = rangePickerProps?.format || DEFAULT_DATA_FORMAT;
          if (afterFormItemType === 'TimePicker' && typeof value === 'string')
            formatObj[value] = timePickerProps?.format || DETAULT_TIME_FORMAT;
        });
        dataFormats.current[afterName] = formatObj;
      }
    }
  };
  const renderChildren = (childrenNodes: any) => {
    let spanNumber: number | undefined = defaultLineNumber
      ? Math.floor(24 / defaultLineNumber)
      : undefined;
    return Children.map(childrenNodes, child => {
      const childElement = child as React.FunctionComponentElement<
        SearchFormItemProps
      >;
      if (!isValidElement(childElement)) return null;
      if (!childElement.type) return null;
      const { displayName } = childElement.type;
      if (displayName && displayName in SearchFormItemEnum) {
        const otherParams: any = { form: form, testId };
        if (displayName !== SearchFormItemEnum.SearchFormOperation) {
          otherParams.span = childElement.props.span || spanNumber;
          otherParams.labelCol = {
            ...defaultLabelCol,
            ...childElement.props.labelCol,
          };
        }
        if (displayName === SearchFormItemEnum.SearchFormGroup) {
          otherParams.defaultLineNumber =
            childElement.props.defaultLineNumber || defaultLineNumber;
          otherParams.defaultLabelCol =
            childElement.props.defaultLabelCol || defaultLabelCol;
          if (Array.isArray(childElement.props.groupOptions)) {
            childElement.props.groupOptions.forEach(groupOption => {
              const groupOptionFormats = getDataFormats(groupOption.children);
              for (let groupOptionName in groupOptionFormats) {
                dataFormats.current[groupOptionName] = groupOptionFormats[groupOptionName];
              }
            });
          }
          if (Array.isArray(childElement.props.children)) {
            childElement.props.children.forEach(groupChild => {
              const groupChildElement = groupChild as React.FunctionComponentElement<
                SearchFormItemProps
              >;
              setformats(groupChildElement);
            });
          }
        }
        if (displayName === SearchFormItemEnum.SearchFormTabs) {
          otherParams.defaultLineNumber =
            childElement.props.defaultLineNumber || defaultLineNumber;
          otherParams.defaultLabelCol =
            childElement.props.defaultLabelCol || defaultLabelCol;
          if (Array.isArray(childElement.props.tabPanes)) {
            childElement.props.tabPanes.forEach(tabsChild => {
              const tabPaneChildrens = tabsChild.children;
              if (Array.isArray(tabPaneChildrens)) {
                renderChildren(tabPaneChildrens);
              }
            });
          }
        }
        if (displayName === SearchFormItemEnum.SearchFormFixed) {
          otherParams.defaultLineNumber =
            childElement.props.defaultLineNumber || defaultLineNumber;
          otherParams.defaultLabelCol =
            childElement.props.defaultLabelCol || defaultLabelCol;
          if (Array.isArray(childElement.props.children)) {
            childElement.props.children.forEach(groupChild => {
              const groupChildElement = groupChild as React.FunctionComponentElement<
                SearchFormItemProps
              >;
              // setformats(groupChildElement);
              renderChildren(groupChildElement);
            });
          }
        }
        if (displayName === SearchFormItemEnum.SearchFormOperation) {
          otherParams.onReset = onReset;
        }
        // if (displayName === SearchFormItemEnum.SearchFormSwitch) {
        //   const switchProps = childElement.props.switchProps || {};
        // }
        setformats(childElement);
        return cloneElement(childElement, otherParams);
      } else {
        console.error(
          `Warning: SearchForm has a child ${displayName} which is not a Item or Group component`,
        );
      }
    });
  };
  const classes = classnames('yb-form', wrapperClassName, {
    [`yb-form-${formType}`]: formType,
    'detail-form-full-page': isFullPage,
  });
  return (
    <div className={classes} style={style}>
      <Form
        form={form}
        onFinish={handleFinish}
        validateTrigger="onBlur"
        className={isFullPage ? 'full-page' : ''}
        {...resProps}
      >
        <SearchFormContext.Provider
          value={{ form, dataFormats: dataFormats.current }}
        >
          {formType === 'search' ? (
            renderChildren(children)
          ) : (
            <Row gutter={24} style={rowStyle} className={isFullPage ? 'full-page' : ''}>
              {renderChildren(children)}
            </Row>
          )}
        </SearchFormContext.Provider>
      </Form>
    </div>
  );
};

SearchForm.defaultProps = {
  filterEmpty: true,
  filterSpace: 'both',
  formType: 'search',
};

export default SearchForm;
